import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { cx } from 'class-variance-authority';
import { useReducedMotion } from 'framer-motion';

import Motion, { type MotionProps } from '@/app/components/Motion';

import type { SectionProps } from './Section';
import SectionContentAppUI from './SectionContentAppUI';
import SectionContentBody from './SectionContentBody';
import SectionContentTime from './SectionContentTime';
import SectionContentTitle from './SectionContentTitle';

export type SectionContentProps = Pick<
  SectionProps,
  | 'activeIndex'
  | 'appUi'
  | 'content'
  | 'heading'
  | 'Icon'
  | 'index'
  | 'time'
  | 'variant'
>;

export const getTranslateMotionProps = ({
  isActive,
  isLargeBreakpoint,
  reducedMotion,
}: {
  isActive: boolean;
  isLargeBreakpoint: boolean;
  reducedMotion: boolean;
}): MotionProps => {
  return isLargeBreakpoint
    ? {
        animate: {
          y: reducedMotion || isActive ? 0 : '-33vh',
          transitionEnd: {
            y: reducedMotion || isActive ? 0 : '33vh',
          },
        },
        initial: {
          y: reducedMotion || isActive ? 0 : '33vh',
        },
      }
    : {
        initial: {
          y: 0,
        },
      };
};

export const getFadeMotionProps = ({
  isActive,
  isLargeBreakpoint,
}: {
  isActive: boolean;
  isLargeBreakpoint: boolean;
}): MotionProps => {
  return isLargeBreakpoint
    ? {
        animate: {
          opacity: isActive ? 1 : 0,
        },
        initial: {
          opacity: 0,
        },
      }
    : {
        initial: {
          opacity: 0,
        },
        viewport: {
          once: true,
        },
        whileInView: {
          opacity: 1,
        },
      };
};

const SectionContent = ({
  activeIndex,
  appUi,
  content,
  heading,
  Icon,
  index,
  time,
  variant,
}: SectionContentProps) => {
  const isActive = activeIndex === index;
  const isLargeBreakpoint = useMediaQuery(
    `(min-width: ${BREAKPOINTS.large}px)`,
  );
  const reducedMotion = useReducedMotion();
  const translateMotionProps = getTranslateMotionProps({
    isActive,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
    reducedMotion: Boolean(reducedMotion),
  });
  const fadeMotionProps = getFadeMotionProps({
    isActive,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
  });

  return (
    <Motion
      {...translateMotionProps}
      className={cx('pointer-events-auto', {
        'lg:js:pointer-events-none': !isActive,
      })}
      data-cy="timeline_section_content"
      transition={{
        duration: 1,
      }}
    >
      {/* Need separate motion element for fade transition to prevent glitch with App UI on mobile */}
      <Motion
        {...fadeMotionProps}
        className="relative z-20"
        transition={{
          duration: 1,
        }}
      >
        <SectionContentTime
          Icon={Icon}
          isActive={isActive}
          time={time}
          variant={variant}
        />
        <SectionContentTitle heading={heading} isActive={isActive} />
        <SectionContentBody content={content} isActive={isActive} />
      </Motion>
      <SectionContentAppUI {...appUi} activeIndex={activeIndex} index={index} />
    </Motion>
  );
};

export default SectionContent;
